/* eslint-disable react/no-danger */
import { useEffect, useRef, useState } from 'react';

import Head from 'next/head';
import { useRouter } from 'next/router';
import ouibounce from 'ouibounce';
import PropTypes from 'prop-types';
import { useIntercom } from 'react-use-intercom';
import { useRecoilState, useRecoilValue } from 'recoil';

import Footer from './footer';
import Loader from './loader';

import useI18n from '../../hooks/use-i18n';
import { countryMap } from '../../language.config';
import { session } from '../../recoil/atoms/session';
import { allTreatments, loader } from '../../recoil/selectors/flightPath';
import { mapDataForIntercom } from '../../utils/flight-path';
import { intercomDisabledPages } from '../../utils/flightpath/config';
import { setCookie } from '../../utils/storage';
import '../../styles/flight-path/layout.scss';
import '../../styles/globals.scss';

const Layout = (props) => {
  const {
    title = 'Zenyum',
    insiderPage,
    page,
    keywords,
    description,
    analyticData,
    pageUrl,
    inAppMessageEnabled,
    customEvent,
    children,
    type,
    showFooter
  } = props;

  const i18n = useI18n();
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';
  const lang = codes[1] || 'en';
  const router = useRouter();
  const query = router && router.query;

  const isLoading = useRecoilValue(loader);
  const [treatments, setTreatments] = useRecoilState(allTreatments);
  const [isMobile, setIsMobile] = useState(false);
  const [userSession, setUserSession] = useRecoilState(session);

  const { boot, update, shutdown } = useIntercom();

  useEffect(() => {
    if (!intercomDisabledPages.includes(page) && userSession.login_type !== 'Admin') {
      boot();
    } else {
      shutdown();
    }
  }, []);

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024.9);
    window.addEventListener('resize', () => {
      setIsMobile(window.innerWidth < 1024.9);
    });
    return () => {
      window.removeEventListener('resize');
    };
  }, []);

  useEffect(() => {
    Object.keys(query).forEach((utmParam) => {
      if (utmParam.startsWith('utm_') || utmParam === 'gclid') {
        setCookie(utmParam, query[utmParam]);
      }
    });
  }, [query]);

  useEffect(() => {
    if (customEvent) {
      customEvent.forEach((segment) => {
        if (process.env.NEXT_PUBLIC_SEGMENT_ID) {
          window.analytics.track(segment);
        }
      });
    }
  }, [customEvent]);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_GTM_ID && window.insider_object) {
      window.insider_object.page = {
        type: insiderPage || page
      };
    }
  }, [page]);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_SEGMENT_ID && (page || analyticData) && page !== 'none') {
      window.analytics?.page(analyticData?.name || page, { ...analyticData?.properties, type: 'page' }, {
        context: {
          location: {
            country: countryMap[country]
          }
        }
      });
      window.analytics.ready(() => {
        window?.appboy?.registerAppboyPushMessages();
        if (inAppMessageEnabled) { window?.appboy?.display?.automaticallyShowNewInAppMessages(); }
        ouibounce(false, {
          callback() { window?.appboy?.logCustomEvent('exit intent'); }
        });
      });
    }
  }, []);

  useEffect(() => {
    initialiseIntercom();
  }, [treatments, userSession]);

  const initialiseIntercom = async () => {
    if (userSession?.loaded && userSession?.user?.id) {
      const currentTreatment = treatments?.find((item) => item.treatment_status === 'INPROGRESS');
      const data = mapDataForIntercom(userSession.user, currentTreatment, lang);
      update(data);
    }
  };

  const getLayoutClass = () => {
    if (['camera', 'sales', 'smartee', 'checkout'].includes(page)) return 'no-margin-layout';
    if (page === 'treatment-plan' || page === 'treatment-plan-sales') return 'margin-layout treatment-plan-mobile';
    if (isMobile && page !== 'flight-path') return 'margin-layout';
    return `${type}-layout`;
  };

  return (
    <div className="wrapper">
      <Head>
        <title>{title}</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="author" content="Zenyum" />
        <meta name="zalo-platform-site-verification" content="PlEiCgJzAWiaq8udnVCzBqV6hJUhjtPjCZ4" />
        <meta httpEquiv="content-language" content={lang} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_BASE_URL}img/FB_zenyum_02.jpg`}
        />
        <link rel="canonical" href={`https://www.zenyum.com/${i18n.activeLocale}${pageUrl ? `/${pageUrl}` : ''}`} />

        <link rel="icon" href={`${process.env.NEXT_PUBLIC_BASE_URL}favicon_32x32.png`} />
        <link rel="icon" href="/favicon_32x32.png" type="image/png" sizes="32x32" />
        <link rel="icon" href="/favicon_192x192.png" type="image/png" sizes="192x192" />
        <link rel="apple-touch-icon" href="/favicon.png" />
        {lang !== 'vi' && lang !== 'th' && lang !== 'zh' && (
          <link rel="stylesheet" href="/fonts/fonts.css" />
        )}
        {lang === 'vi' && <link rel="stylesheet" href="/fonts/fonts-vi.css" />}
        {lang === 'th' && <link rel="stylesheet" href="/fonts/fonts-th.css" />}
        {lang === 'zh' && <link rel="stylesheet" href="/fonts/fonts-zh.css" />}
        {lang === 'ja' && <link rel="stylesheet" href="/fonts/fonts-jp.css" />}
        {lang === 'id' && <link rel="stylesheet" href="/fonts/fonts-id.css" />}
        <link rel="stylesheet" href="/vendor/bootstrap/css/bootstrap.min.css" async={true} />
        {process.env.NEXT_PUBLIC_INSIDER_PARTNER_NAME && (
          <script
            async={true}
            src={`//${process.env.NEXT_PUBLIC_INSIDER_PARTNER_NAME}.api.useinsider.com/ins.js?id=
          ${process.env.NEXT_PUBLIC_INSIDER_PARTNER_ID}`}
          />
        )}
        <script
          dangerouslySetInnerHTML={{
            __html: `document.documentElement.style.setProperty('overflow', 'unset', 'important');`
          }}
        />
        {/* End (noscript) */}
      </Head>

      {/* Google Tag Manager (noscript) */}
      {process.env.NEXT_PUBLIC_GTM_ID && (
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${process.env.NEXT_PUBLIC_GTM_ID}`}
            height="0"
            width="0"
            title="gtag"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
      )}
      {/* End Google Tag Manager (noscript) */}

      {/* Commented out Intercom Provider , Since intercom has been moved to the header */}
      {/* <IntercomProvider
        isActive={!intercomDisabledPages.includes(page) && userSession.login_type !== 'Admin'}
        whatsAppEnabled={whatsappEnabledPages.includes(page)}
      > */}
      <main className={`flight-path-wrapper ${getLayoutClass()}`}>
        {isLoading && (
        <div className="main-loader">
          <Loader />
        </div>
        )}
        {children}
        {((type === 'margin' || isMobile) && showFooter) && <Footer
          i18n={i18n}
          countryMap={countryMap}
          country={country}
          page={page}
        />}
      </main>
      {/* </IntercomProvider> */}
    </div>
  );
};

export default Layout;

Layout.propTypes = {
  analyticData: PropTypes.shape(),
  children: PropTypes.element,
  customEvent: PropTypes.arrayOf(),
  description: PropTypes.string,
  inAppMessageEnabled: PropTypes.bool,
  insiderPage: PropTypes.string,
  keywords: PropTypes.string,
  page: PropTypes.string,
  pageUrl: PropTypes.string,
  showHeader: PropTypes.bool,
  title: PropTypes.string,
  type: PropTypes.oneOf(['login', 'margin', 'no-margin']).isRequired,
  showFooter: PropTypes.bool
};

Layout.defaultProps = {
  children: <></>,
  title: 'Zenyum',
  page: 'none',
  insiderPage: '',
  description: 'Zenyum',
  keywords: 'Zenyum',
  analyticData: {},
  inAppMessageEnabled: true,
  pageUrl: ' ',
  customEvent: [],
  showHeader: true,
  showFooter: true
};
